"use client";
import { cva, type VariantProps } from "class-variance-authority";
import Link from "next/link";
import { ComponentPropsWithoutRef, forwardRef } from "react";
import { cn } from "~/utils/fontsConfig";

const styleClassNames = {
    shared: `
        rounded-full
        w-max
        h-max
        gap-2
        inline-flex 
        items-center 
        justify-center 
        font-normal 
        ring-offset-background 
        transition-colors 
        focus-visible:outline-none 
        focus-visible:ring-2 
        focus-visible:ring-ring 
        focus-visible:ring-offset-2 
        disabled:cursor-not-allowed
    `,
    default: `
        text-v3-primary
        justify-start
        p-0 
        hover:underline
    `,
    secondary: `
        text-white
        hover:underline
        justify-start 
        p-0 
        hover:text-v3-N-200
    `,
    tertiary: `
        text-v3-N-400
        justify-start
        p-0 
        hover:underline
    `,
};

const linkVariants = cva(styleClassNames.shared, {
    variants: {
        variant: {
            default: styleClassNames.default,
            secondary: styleClassNames.secondary,
            tertiary: styleClassNames.tertiary,
        },
        size: {
            default: "p-0",
            sm: "h-9 rounded-md",
            xs: "h-5 rounded-md",
            lg: "h-11 rounded-md",
            icon: "h-10 w-10",
            breadcrumb: "p-0 text-2xl font-semibold",
        },
    },
    defaultVariants: {
        variant: "default",
        size: "default",
    },
});

interface ExtendedLinkProps
    extends ComponentPropsWithoutRef<typeof Link>,
        VariantProps<typeof linkVariants> {}

const ThemedLink = forwardRef<HTMLAnchorElement, ExtendedLinkProps>(
    ({ className, variant, size, ...restProps }: ExtendedLinkProps, ref) => {
        return (
            <Link
                ref={ref}
                className={cn(linkVariants({ variant, size, className }))}
                {...restProps}
            />
        );
    },
);

ThemedLink.displayName = "ThemedLink";

export default ThemedLink;
